const HISTORY_ARRAY_KEYS = [
  "lineComplaintHistory",
  "complaintHistory",
  "ComplaintHistory",
  "complaintHist",
  "history",
  "History",
  "hist",
  "Hist",
  "statusHistory",
  "StatusHistory",
];

function pickFirst(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return null;
}

function getComplaintKey(row = {}) {
  return (
    pickFirst(
      row.EOpsTrackingId,
      row.eOpsTrackingId,
      row.TicketNo,
      row.ticketNo,
      row.RefNo,
    ) || "unknown"
  );
}

export function isHistoryEventRow(row = {}) {
  return Boolean(
    pickFirst(
      row.Status1,
      row.status1,
      row.ModificationDateTime1,
      row.modificationDateTime1,
    ),
  );
}

/** Convert a flat API history row (Status1 / ModificationDateTime1) into a history event. */
export function historyEventFromApiRow(row = {}) {
  return {
    Status: pickFirst(row.Status1, row.status1, row.Status, row.status),
    ModificationDateTime: pickFirst(
      row.ModificationDateTime1,
      row.modificationDateTime1,
      row.ModificationDateTime,
      row.RegisteredAt,
    ),
    ModifiedBy: pickFirst(
      row.ModifiedBy1,
      row.modifiedBy1,
      row.ModifiedBy,
      row.RegisteredBy,
    ),
    Remarks: pickFirst(
      row.LinemanRemarks1,
      row.ConsumerRemarks1,
      row.LinemanRemarks,
      row.Remarks,
      row.ConsumerRemarks,
    ),
    LinemanRemarks: pickFirst(row.LinemanRemarks1, row.LinemanRemarks),
    ConsumerRemarks: pickFirst(row.ConsumerRemarks1, row.ConsumerRemarks),
    UserFullName: pickFirst(row.UserFullName, row.userFullName),
    UserId: pickFirst(row.UserId1, row.UserId),
    PhoneNo: pickFirst(row.PhoneNo, row.phoneNo),
    AllocationDateTime: row.AllocationDateTime || null,
  };
}

/**
 * API often returns one row per history event (same ticket repeated).
 * Group into unique complaints with `lineComplaintHistory` attached.
 */
export function groupComplaintHistoryApiRows(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const hasEventShape = rows.some(isHistoryEventRow);
  if (!hasEventShape) {
    return rows;
  }

  const grouped = new Map();

  rows.forEach((row) => {
    const key = getComplaintKey(row);
    if (!grouped.has(key)) {
      grouped.set(key, {
        ...row,
        lineComplaintHistory: [],
      });
    }

    const complaint = grouped.get(key);
    if (isHistoryEventRow(row)) {
      complaint.lineComplaintHistory.push(historyEventFromApiRow(row));
    }
  });

  return Array.from(grouped.values());
}

export function extractComplaintHistory(complaint = {}) {
  if (!complaint || typeof complaint !== "object") return [];

  for (const key of HISTORY_ARRAY_KEYS) {
    const value = complaint[key];
    if (Array.isArray(value) && value.length > 0) {
      return value;
    }
  }

  if (isHistoryEventRow(complaint)) {
    return [historyEventFromApiRow(complaint)];
  }

  return buildFallbackComplaintHistory(complaint);
}

export function buildFallbackComplaintHistory(complaint = {}) {
  const rows = [];

  if (complaint.RegisteredAt) {
    rows.push({
      Status: "Pending",
      ModificationDateTime: complaint.RegisteredAt,
      ModifiedBy: complaint.RegisteredBy || complaint.Name || complaint.Source || "-",
      Remarks: "Ticket Registered",
      UserFullName: complaint.UserFullName || "-",
    });
  }

  if (complaint.AllocationDateTime) {
    rows.push({
      Status: "Allocated",
      ModificationDateTime: complaint.AllocationDateTime,
      ModifiedBy: complaint.ModifiedBy || "-",
      Remarks: complaint.LinemanRemarks || "Complaint allocated.",
      UserFullName: complaint.UserFullName || "-",
    });
  }

  if (complaint.ModificationDateTime && complaint.Status) {
    const last = rows[rows.length - 1];
    const lastDate = last?.ModificationDateTime;
    const isDuplicate =
      lastDate &&
      new Date(lastDate).getTime() ===
        new Date(complaint.ModificationDateTime).getTime();

    if (!isDuplicate) {
      rows.push({
        Status: complaint.Status,
        ModificationDateTime: complaint.ModificationDateTime,
        ModifiedBy: complaint.ModifiedBy || "-",
        Remarks: complaint.LinemanRemarks || complaint.Remarks || "-",
        UserFullName: complaint.UserFullName || "-",
      });
    }
  }

  return rows;
}

export function normalizeComplaintHistoryRow(row = {}, index = 0) {
  const status = pickFirst(
    row.Status,
    row.status,
    row.Status1,
    row.status1,
    row.complaintStatus,
    row.ComplaintStatus,
  );

  const dateTime = pickFirst(
    row.ModificationDateTime,
    row.modificationDateTime,
    row.ModificationDateTime1,
    row.modificationDateTime1,
    row.dateTime,
    row.LastModificationDate,
    row.lastModificationDate,
    row.RegisteredAt,
    row.registeredAt,
  );

  const processedBy = pickFirst(
    row.ModifiedBy,
    row.modifiedBy,
    row.ModifiedBy1,
    row.modifiedBy1,
    row.ProcessedBy,
    row.processedBy,
    row.RegisteredBy,
  );

  const assignedTo = pickFirst(
    row.UserFullName,
    row.userFullName,
    row.allocatedTo,
    row.AssignedTo,
  );

  const remarks = pickFirst(
    row.Remarks,
    row.remarks,
    row.LinemanRemarks,
    row.linemanRemarks,
    row.LinemanRemarks1,
    row.ConsumerRemarks1,
    row.ConsumerRemarks,
  );

  const actionLabel = buildHistoryActionLabel({
    status,
    processedBy,
    remarks,
  });

  return {
    srNo: index + 1,
    status: status || "-",
    processedBy: processedBy || "-",
    dateTime: dateTime || null,
    assignedTo: assignedTo || "-",
    remarks: remarks || "-",
    actionLabel,
  };
}

export function buildHistoryActionLabel({ status, processedBy, remarks } = {}) {
  const actor = processedBy && processedBy !== "-" ? processedBy : "System";
  const statusText = status && status !== "-" ? status : "Updated";
  const remarkText =
    remarks && remarks !== "-" ? ` [${remarks}]` : "";

  if (String(statusText).toLowerCase() === "pending") {
    return `${actor} changed status to ${statusText}${remarkText || " [Ticket Registered]"}`;
  }

  return `${actor} changed status to ${statusText}${remarkText}`;
}

export function sortComplaintHistoryRows(rows = []) {
  return [...rows].sort((a, b) => {
    const aMs = a.dateTime ? new Date(a.dateTime).getTime() : 0;
    const bMs = b.dateTime ? new Date(b.dateTime).getTime() : 0;
    return bMs - aMs;
  });
}
